import { useCallback, useState, type MouseEvent, type RefObject } from 'react'
import type { SeriesTooltipState } from './ChartTooltip.types'

type PlotPoint = { x: number; y: number }

type UseSeriesChartTooltipArgs = {
  enabled: boolean
  containerRef: RefObject<HTMLElement | null>
  /** Plot points in viewBox coordinates. */
  plotPoints: PlotPoint[]
  /** ViewBox width used to map client → plot space. */
  viewWidth: number
  /** ViewBox height used to map client → plot space. */
  viewHeight: number
}

type UseSeriesChartTooltipResult = {
  tooltip: SeriesTooltipState
  onMouseMove: (event: MouseEvent<HTMLElement>) => void
  onMouseLeave: () => void
}

const HIDDEN: SeriesTooltipState = {
  visible: false,
  index: -1,
  x: 0,
  y: 0,
  pointX: 0,
  pointY: 0,
}

/**
 * Nearest-point hover for area / line series charts.
 * Maps pointer position through the SVG viewBox → container px.
 */
export const useSeriesChartTooltip = ({
  enabled,
  containerRef,
  plotPoints,
  viewWidth,
  viewHeight,
}: UseSeriesChartTooltipArgs): UseSeriesChartTooltipResult => {
  const [tooltip, setTooltip] = useState<SeriesTooltipState>(HIDDEN)

  const hide = useCallback(() => setTooltip(HIDDEN), [])

  const onMouseMove = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (
        !enabled ||
        plotPoints.length === 0 ||
        viewWidth <= 0 ||
        viewHeight <= 0
      ) {
        hide()
        return
      }

      const container = containerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return

      const localX = event.clientX - rect.left
      const localY = event.clientY - rect.top
      const plotX = (localX / rect.width) * viewWidth

      let nearestIndex = 0
      let nearestDist = Number.POSITIVE_INFINITY
      for (let i = 0; i < plotPoints.length; i += 1) {
        const dist = Math.abs(plotPoints[i].x - plotX)
        if (dist < nearestDist) {
          nearestDist = dist
          nearestIndex = i
        }
      }

      const point = plotPoints[nearestIndex]
      const pointX = (point.x / viewWidth) * rect.width
      const pointY = (point.y / viewHeight) * rect.height

      setTooltip({
        visible: true,
        index: nearestIndex,
        x: localX,
        y: localY,
        pointX,
        pointY,
      })
    },
    [containerRef, enabled, hide, plotPoints, viewHeight, viewWidth],
  )

  return { tooltip, onMouseMove, onMouseLeave: hide }
}
